/** Admin SPA prefix — company auth stays under `/company`. */
export const ADMIN_PREFIX = "/admin";
export const COMPANY_PREFIX = "/company";
export const CLIENT_PREFIX = "/client";

/** Joins a URL prefix with a path segment (normalizes leading `/` on `path`). */
const routeUnder = (prefix, path) => {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${prefix}${p}`;
};

const admin = (path) => routeUnder(ADMIN_PREFIX, path);
const company = (path) => routeUnder(COMPANY_PREFIX, path);
const client = (path) => routeUnder(CLIENT_PREFIX, path);

export const ROUTE_PATHS = {
  /** Bare `/admin` — redirect to `login`. */
  adminRoot: ADMIN_PREFIX,
  login: admin("/login"),
  /** Bare `/company` — use redirect to `companyLogin`. */
  companyRoot: COMPANY_PREFIX,
  companyLogin: company("/login"),
  companyLoginOtp: company("/login/otp"),
  companyLoginCompany: company("/login/company"),
  companySignup: company("/signup"),
  companySignupContact: company("/signup/contact"),
  companySignupOtp: company("/signup/otp"),
  companySignupCompany: company("/signup/company"),
  companyPendingApproval: company("/pending-approval"),
  /** Blocked login screen for a user of an Inactive company. */
  companyDeactivated: company("/deactivated"),
  companyResetPassword: company("/reset-password"),
  companyForgetPassword: company("/forget-password"),
  companyResetPasswordSuccess: company("/reset-password/success"),
  companyChangePassword: company("/change-password"),
  forgetPassword: admin("/forget-password"),
  resetPasswordSuccess: admin("/reset-password/success"),
  changePassword: admin("/change-password"),
  resetPassword: admin("/profile/reset-password"),
  reviewProfileUpdateRequest: admin("/companies/:id/review-profile-update-request"),

  home: admin("/home"),
  /** Super Admin dashboard. */
  adminDashboard: admin("/dashboard"),
  /** Super Admin full activity log (dashboard → Recent Activity Log → Full Logs). */
  adminActivityLog: admin("/activity-log"),
  events: admin("/events"),
  createEvent: admin("/events/create"),
  viewEvent: admin("/events/:id/view"),
  inviteApplicants: admin("/events/:id/view/invite-applicants"),
  editEvent: admin("/events/:id/edit"),
  adminApplicantEvaluationResult: admin(
    "/events/:eventId/evaluation-rounds/:roundId/applicants/:applicantId/result",
  ),
  adminEvaluateApplicant: admin(
    "/events/:eventId/evaluation-rounds/:roundId/applicants/:applicantId/evaluate",
  ),
  employees: admin("/employees"),
  viewEmployee: admin("/employees/:id/view"),
  editEmployees: admin("/employees/editemployees/:id"),

  profile: admin("/profile"),
  editProfile: admin("/profile/edit"),
  requestsEvents: admin("/joining-festivities"),
  viewRequests: admin("/joining-festivities/:id/joining-requests"),
  viewRequestsApplicant: admin("/joining-festivities/applicant/:id"),
  promotionRequests: admin("/promotions-requests"),
  demoRequests: admin("/demo-requests"),
  addProjectManager: admin("/employees/add-project-manager"),
  applicants: admin("/applicants"),
  viewApplicant: admin("/applicants/:id/profile"),
  viewApplicantLegacy: admin("/applicants/:id"),
  editApplicantProfile: admin("/applicant/:id"),
  viewApplicantRequests: admin("/applicants/:id/pending-requests"),
  viewApplicantEvents: admin("/applicants/:id/events"),
  viewApplicantEvaluationView: admin(
    "/applicants/:id/events/evaluation/:formid/view",
  ),
  viewApplicantEvaluationEdit: admin(
    "/applicants/:id/events/evaluation/:formid/edit",
  ),
  timesheet: admin("/timesheet"),
  monthlyReport: admin("/timesheet/:eventid/monthly-report"),
  dailyReport: admin("/timesheet/:eventid/daily-report"),
  applicantReport: admin("/timesheet/:eventid/applicant-report/:userid"),
  editEvaluations: admin("/evaluations/:id/edit"),
  notifications: admin("/notification"),
  locations: admin("/locations"),
  reports: admin("/reports"),
  viewReport: admin("/reports/:id/view"),
  viewReportDetail: admin("/reports/:event_id/report-detail/:id"),
  noPermission: admin("/no-permission"),

  clientSurvey: admin("/client-survey"),
  clientSurveyByID: admin("/client-survey/:id"),
  adminCompanyViewClient: admin("/companies/:companyId/clients/:id/view"),
  adminCompanyAddClient: admin("/companies/:companyId/clients/create"),
  adminCompanyEditClient: admin("/companies/:companyId/clients/:id/edit"),
  adminCompanyTeamEmployeeCreate: admin(
    "/companies/:companyId/team/employees/create",
  ),
  adminCompanyTeamEmployeeView: admin(
    "/companies/:companyId/team/employees/:id",
  ),
  adminCompanyTeamRoleCreate: admin("/companies/:companyId/team/roles/create"),
  adminCompanyTeamRoleView: admin("/companies/:companyId/team/roles/:id"),
  adminSurveyScore: admin("/admin-survey-score"),
  adminSurveyScoreDetail: admin("/admin-survey-score/:id"),

  roleManagement: admin("/roles-management"),
  viewRoleManagement: admin("/roles-management/:id/view"),
  adminclientSurvey: admin("/admin-survey-events"),
  adminClientEventSurvey: admin("/admin-survey-events/:id"),
  viewEventSurvey: admin("/event-survey/:id"),
  viewEventHeatMap: admin("/event-heatmap/:id"),

  registrationRequests: admin("/companies/registration-requests"),
  registrationRequestCompanyView: admin("/companies/registration-requests/:id"),
  profileEditRequests: admin("/profile-edit-requests"),
  publishingRequests: admin("/publishing-requests"),
  companiesList: admin("/companies"),
  companiesListCompanyDetails: admin("/companies/:id"),
  companiesListCompanyClients: admin("/companies/:id/clients"),
  companiesListCompanyEdit: admin("/companies/:id/edit"),

  /** Logged-in company portal (session `company`) — under `/company`, not the auth shell. */
  companyDashboard: company("/dashboard"),
  /** Company full activity log (dashboard → Recent Activity Log → Full Logs). */
  companyActivityLog: company("/activity-log"),
  /** Default landing page after company portal sign-in. */
  companyHome: company("/events"),
  companyEvents: company("/events"),
  companyCreateEvent: company("/events/create"),
  companyEditEvent: company("/events/:id/edit"),
  companyViewEvent: company("/events/:id/view"),
  companyClients: company("/clients"),
  companyAddClient: company("/clients/add"),
  companyViewClient: company("/clients/:id/view"),
  companyEditClient: company("/clients/:id/edit"),
  companyApplicants: company("/applicants"),
  companyViewApplicantProfile: company("/applicants/:id/profile"),
  companyEditApplicantProfile: company("/applicants/:id/edit"),
  companySettingsProfile: company("/settings/profile"),
  companySettingsProfileEdit: company("/settings/profile/edit"),
  companyTeamsPermissions: company("/teams-permissions"),
  companyTeamRoleCreate: company("/teams-permissions/roles/create"),
  companyTeamRoleView: company("/teams-permissions/roles/:id"),
  companyTeamEmployeeCreate: company("/teams-permissions/employees/create"),
  companyTeamEmployeeView: company("/teams-permissions/employees/:id"),
  companySettingsSecurity: company("/settings/security"),
  companyNotifications: company("/notifications"),
  companyLoyalApplicants: company("/loyal-applicants"),
  companyReports: company("/reports"),
  companyViewReport: company("/reports/:id/view"),
  /** Empty-state landing for a company employee whose role can view nothing. */
  companyNoPermission: company("/no-permission"),
  companyEventEvaluationRound: company("/events/:eventId/view/evaluation-rounds/:roundId"),
  companyEvaluateApplicant: company("/events/:eventId/evaluation-rounds/:roundId/applicants/:applicantId/evaluate"),
  ApplicantEvaluationResult: company("/events/:eventId/evaluation-rounds/:roundId/applicants/:applicantId/result"),

  /** Logged-in client portal. */
  clientRoot: CLIENT_PREFIX,
  clientLogin: client("/login"),
  clientSelectCompany: client("/select-company"),
  clientForgetPassword: client("/forget-password"),
  clientResetPassword: client("/reset-password"),
  clientResetPasswordSuccess: client("/reset-password/success"),
  clientChangePassword: client("/change-password"),
  clientSettingsSecurity: client("/settings/security"),
  clientPortalDashboard: client("/dashboard"),
  clientPortalEvents: client("/events"),
  clientPortalViewEvent: client("/events/:id/view"),
  clientPortalFinance: client("/finance"),
  clientPortalReports: client("/reports"),
  clientSurveyPortal: client("/survey"),
  clientNotifications: client("/notifications"),
};
